import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { createAnimation, animations } from "./animations";

const QRScannerContainer = styled.div`
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1002;
  opacity: 0;
  animation: ${createAnimation(animations.qrScanner)};
  animation-delay: ${animations.qrScanner.delay};
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
  transition: all 0.8s ease;

  &.move-to-position {
    @media (max-width: 768px) {
      top: auto;
      bottom: -1rem;
      transform: translateX(-50%);
      transition: all 3s cubic-bezier(0.4, 0, 0.2, 1); /* Even slower and smoother transition for mobile */
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 180px;
  height: 180px;
  background-color: white;
  border-radius: 12px;
  padding: 8px;
  box-sizing: border-box;
  user-select: none;

  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
  }
`;

const QRImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: all 0.5s ease-in-out;
  transform: ${(props) => (!props.$isAnimating ? "scale(1.2)" : "scale(1)")};
  user-select: none;
  pointer-events: none;
`;

const QRScanner = ({ onAnimationComplete, className, isNetworkError }) => {
  const [animationCount, setAnimationCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [maxAnimations, setMaxAnimations] = useState(1); // Initialize with minimum value

  // Calculate GIF duration (assuming it's 2 seconds, adjust as needed)
  const gifDuration = 2000; // 2 seconds

  // Generate weighted random number of animations
  useEffect(() => {
    if (isNetworkError) {
      // For network errors, always play once
      setMaxAnimations(1);
    } else {
      // For verified and unverified products, use weighted random
      const getWeightedRandom = () => {
        const random = Math.random();
        if (random < 0.66) return 1; // 66% chance for 1
        if (random < 0.99) return 2; // 33% chance for 2
        return 3; // 1% chance for 3
      };

      const randomAnimations = getWeightedRandom();
      setMaxAnimations(randomAnimations);
    }
  }, [isNetworkError]);

  useEffect(() => {
    if (animationCount < maxAnimations) {
      const timer = setTimeout(() => {
        setAnimationCount((prev) => prev + 1);
      }, gifDuration);

      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      // Notify parent component that animation is complete
      if (onAnimationComplete) {
        onAnimationComplete();
      }
    }
  }, [animationCount, onAnimationComplete, maxAnimations]);

  return (
    <QRScannerContainer className={className}>
      <ImageContainer>
        <QRImage
          src={
            isAnimating ? "/images/qr-code.gif" : "/images/qr-code-static.png"
          }
          alt="QR Code"
          $isAnimating={isAnimating}
        />
      </ImageContainer>
    </QRScannerContainer>
  );
};

export default QRScanner;
